import { useEffect, useState } from 'react';
import { operatorConsole, type TrackingPixels } from '../../lib/api/operatorConsole';
import { ConsentField } from './ConsentField';
import { EventsTable } from './EventsTable';
import { LimitationsNote } from './LimitationsNote';
import { PixelField } from './PixelField';
import { ProviderCard } from './ProviderCard';
import { PROVIDERS, REPORT_CURRENCY_FIELD } from './providers';
import { VerifyGuide } from './VerifyGuide';
import {
  formatErrors,
  hasAnyProvider,
  toForm,
  toPayload,
  type TrackingFieldName,
  type TrackingPixelsForm,
} from './trackingPixelsForm';

const SUCCESS_FLASH_MS = 2500;

function errorText(err: unknown): string {
  return err instanceof Error ? err.message : String(err);
}

export function TrackingPixelsPage() {
  const [saved, setSaved] = useState<TrackingPixels>({});
  const [form, setForm] = useState<TrackingPixelsForm>(() => toForm({}));
  const [consentTicked, setConsentTicked] = useState(false);
  const [loading, setLoading] = useState(true);
  // PUT replaces the whole config, so an empty form shown after a failed load
  // would wipe the saved pixels on the next save.
  const [loadFailed, setLoadFailed] = useState(false);
  const [loadAttempt, setLoadAttempt] = useState(0);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    void operatorConsole
      .getTrackingPixels()
      .then((config) => {
        if (!alive) return;
        setSaved(config);
        setForm(toForm(config));
      })
      .catch((err: unknown) => {
        if (!alive) return;
        setLoadFailed(true);
        setError(`Failed to load pixels: ${errorText(err)}`);
      })
      .finally(() => {
        if (alive) setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, [loadAttempt]);

  function retryLoad() {
    setLoading(true);
    setLoadFailed(false);
    setError(null);
    setLoadAttempt((attempt) => attempt + 1);
  }

  const errors = formatErrors(form);
  const consentMissing = hasAnyProvider(form) && !saved.consent && !consentTicked;
  const canSave = !saving && Object.keys(errors).length === 0 && !consentMissing;

  function updateField(field: TrackingFieldName, value: string) {
    setForm((prev) => ({ ...prev, [field]: value }));
  }

  async function handleSave() {
    try {
      setSaving(true);
      setError(null);
      const config = await operatorConsole.putTrackingPixels(toPayload(form), consentTicked);
      setSaved(config);
      setForm(toForm(config));
      setConsentTicked(false);
      setSuccess('Pixels saved');
      setTimeout(() => setSuccess(null), SUCCESS_FLASH_MS);
    } catch (err) {
      setError(`Save failed: ${errorText(err)}`);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="p-6">
      <div className="mb-5 flex items-baseline justify-between gap-4">
        <div>
          <h1 className="text-[20px] font-semibold text-[var(--co-text)]">Tracking Pixels</h1>
          <p className="text-[12px] text-[var(--co-muted)]">
            Your own ad pixels, loaded inside the game for your players. Enter the ids only — we load each
            vendor&apos;s official script.
          </p>
        </div>
        {(error || success) && (
          <span
            className={`rounded px-2 py-1 text-[11px] font-medium ${
              error ? 'bg-[var(--co-error)] text-white' : 'bg-[var(--co-success)] text-white'
            }`}
          >
            {error ?? success}
          </span>
        )}
      </div>

      {loading ? (
        <p className="text-[13px] text-[var(--co-muted)]">Loading pixels…</p>
      ) : loadFailed ? (
        <div className="max-w-2xl rounded-xl border border-[var(--co-border)] bg-[var(--co-surface)] p-4">
          <p className="mb-3 text-[13px] text-[var(--co-text)]">
            Your saved pixels could not be loaded, so editing is off — saving now would replace them.
          </p>
          <button
            type="button"
            onClick={retryLoad}
            className="rounded-lg border border-[var(--co-border)] px-3 py-2 text-[12px] font-medium text-[var(--co-text)] hover:bg-[var(--co-border)]/30"
          >
            Retry
          </button>
        </div>
      ) : (
        <div className="flex max-w-2xl flex-col gap-4">
          <LimitationsNote />
          <EventsTable />
          {PROVIDERS.map((spec) => (
            <ProviderCard key={spec.key} spec={spec} form={form} errors={errors} onChange={updateField} />
          ))}
          <section className="rounded-xl border border-[var(--co-border)] bg-[var(--co-surface)] p-4">
            <PixelField
              spec={REPORT_CURRENCY_FIELD}
              value={form.reportCurrency}
              error={errors.reportCurrency}
              onChange={(value) => updateField('reportCurrency', value)}
            />
            <p className="mt-1 text-[11px] text-[var(--co-muted)]">
              Only needed when your platform currency is not an ISO code (SC, GC…). Left empty, such amounts are
              reported in USD.
            </p>
          </section>
          <ConsentField consent={saved.consent} ticked={consentTicked} onChange={setConsentTicked} />
          <div>
            <button
              type="button"
              onClick={() => void handleSave()}
              disabled={!canSave}
              className="rounded-lg bg-[var(--co-brand)] px-4 py-2 text-[12px] font-semibold text-white hover:opacity-90 disabled:opacity-50"
            >
              {saving ? 'Saving…' : 'Save'}
            </button>
          </div>
          <VerifyGuide />
        </div>
      )}
    </div>
  );
}
